<script lang="ts">
  import { screens, playerTruth } from '../store';
  import Portrait from './components/portrait.svelte';
  import { tweened } from 'svelte/motion';
  import { addModifier } from './models/player.type';
  import { StatusEffect } from './models/status-effects.type';
  import { track } from './utils/playerTracking';
  import Frame from './components/frame.svelte';
  import Chrome from './components/chrome.svelte';
  import Icon from './icon/icon.svelte';
  import { Screen } from './models/screen.type';

  interface Props {
    data: any;
  }

  let { data }: Props = $props();

  const won = data.battle.outcome === 'win';

  let experienceGain = 5 + data.battle.level * 5;
  let moneyGain = 5;

  // reduce modifiers duraction by 1
  $playerTruth.statusEffects.forEach((modifier) => {
    if (modifier.duration) {
      modifier.duration--;
    }
  });
  $playerTruth.statusEffects = $playerTruth.statusEffects.filter((modifier) => {
    return modifier.duration > 0;
  });

  let experienceUI = tweened($playerTruth.experience);
  let moneyUI = tweened($playerTruth.money);

  if (won) {
    $playerTruth.experience += experienceGain;
    $playerTruth.money += moneyGain;
  } else {
    addModifier($playerTruth, StatusEffect.Wound, 1);
  }

  setTimeout(() => {
    experienceUI.set($playerTruth.experience);
    setTimeout(() => {
      moneyUI.set($playerTruth.money);
    }, 500);
  }, 1000);

  function back() {
    screens.pop();

    // resolve promise?
    if (data.battle.resolve) {
      data.battle.resolve(data.battle.outcome);
    }
  }

  let experienceRequired = ($playerTruth.level + 1) * 5 * 3;
  function levelUp() {
    if ($playerTruth.experience >= experienceRequired) {
      $playerTruth.experience -= experienceRequired;
      experienceUI.set($playerTruth.experience);
      screens.push(Screen.Upgrade);
    }
  }

  track('Battle Over', {
    outcome: data.battle.outcome,

    player_class: $playerTruth.type,
    player_archetype: $playerTruth.archetype,
    player_level: $playerTruth.level,

    enemy_class: data.battle.enemy.type,
    enemy_level: data.battle.enemy.level,
  });
</script>

<div class="background {data.battle.environment}"></div>

<div class="stage">
  <Frame>
    <p class="step-title">Battle Over</p>

    <h1 class="outcome" class:win={won}>{won ? 'Victory' : 'Defeat'}</h1>

    <div class="result">
      <div class="side">
        <Portrait data={$playerTruth} />
        <h2>{$playerTruth.title}</h2>
        <span class="stat">Level {$playerTruth.level + 1}</span>
        <span class="stat">{$playerTruth.healthCurrent}/{$playerTruth.healthMax} HP</span>
      </div>

      <div class="spoils" class:win={won}>
        <div class="row">
          <div class="stat-label">
            <Icon name="level" />
            <h3>Experience</h3>
            <span class="rule"></span>
            {#if won}
              <span class="stat-value">+{experienceGain}</span>
            {/if}
          </div>
          <div
            class="bar-container yellow"
            class:ready={$experienceUI >= experienceRequired}
            role="button"
            tabindex="0"
            aria-disabled={$experienceUI < experienceRequired}
            onclick={levelUp}
            onkeydown={() => {}}
          >
            <div
              class="bar-inner"
              style="width: {Math.min(100, ($experienceUI / experienceRequired) * 100)}%"
            ></div>
            {#if $experienceUI >= experienceRequired}
              <p>Level Up!</p>
            {:else}
              <p>{Math.round($experienceUI)} / {experienceRequired}</p>
            {/if}
          </div>
        </div>

        <div class="row">
          <div class="stat-label">
            <Icon name="coin" />
            <h3>Money</h3>
            <span class="rule"></span>
            {#if won}
              <span class="stat-value">+{moneyGain}</span>
            {/if}
          </div>
          <div class="plaque">
            <p>{Math.round($moneyUI)}</p>
          </div>
        </div>

        {#if !won}
          <p class="wounded">
            <Icon name="wound" />
            Wounded: your max health drops by 1.
          </p>
        {/if}
      </div>
    </div>

    <div class="button-container">
      <Chrome click={back} edge="circle" chroming="gold">
        <div class="button">Continue</div>
      </Chrome>
    </div>
  </Frame>
</div>

<style lang="less">
  @import '../defines.less';
  @import '../styles/bars.less';

  // Landscape phones: too short to stack the player over their spoils.
  @short: ~'(orientation: landscape) and (max-height: 560px)';

  :root {
    background-color: var(--background-colour);
  }

  .background {
    height: 100vh;
    width: 100vw;
    position: absolute;
    top: 0;
    left: 0;
    filter: blur(5px);
    z-index: 0;
    box-shadow: inset 0 0 40px 10px rgba(0, 0, 0, 1);
  }

  // Sized and spaced as battle start is, so the two ends of a battle match.
  .stage {
    --column-size: clamp(90px, min(28vw, 26vh), 240px);

    position: absolute;
    inset: 0;
    display: grid;
    box-sizing: border-box;
    padding: 12px;
    overflow-y: auto;
    text-align: center;
    text-shadow: 2px 2px black;
  }

  .step-title {
    margin: 0;
    font-size: clamp(0.95em, min(3.6vw, 2.4vh), 1.3em);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  // Battle start's gold VS, grown into the headline.
  .outcome {
    margin: clamp(2px, 1vh, 10px) 0 clamp(10px, 2.5vh, 24px);
    font-size: clamp(2em, min(9vw, 7vh), 3.6em);
    font-weight: 700;
    font-style: italic;
    line-height: 1.1;
    color: @red;

    &.win {
      color: @edge-shine;
    }
  }

  .result {
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-gap: clamp(10px, 2.5vh, 28px);

    @media @short {
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }

  .side {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: var(--column-size);
    grid-gap: 4px;

    h2 {
      margin-top: clamp(4px, 1vh, 10px);
      font-size: clamp(1.1em, min(4.5vw, 3.6vh), 1.8em);
      line-height: 1.1;
    }

    .stat {
      font-size: clamp(12px, 1.7vh, 15px);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      opacity: 0.7;
    }
  }

  // Battle start's dossier panel, pointing up at the player this time. Edged
  // gold for a win, red for a loss.
  .spoils {
    --accent: @red;

    position: relative;
    display: flex;
    flex-direction: column;
    grid-gap: clamp(12px, 2.2vh, 20px);
    width: 100%;
    max-width: 360px;
    box-sizing: border-box;
    padding: clamp(12px, 2vh, 18px) clamp(14px, 2vw, 20px);
    text-align: left;
    text-shadow: none;
    background: @panel-sunken;
    border: 1px solid var(--accent);
    border-radius: 6px;
    box-shadow:
      inset 0 0 14px fade(@black, 70%),
      0 0 6px fade(@black, 60%);

    &.win {
      --accent: @yellow;
    }

    &::before {
      content: '';
      position: absolute;
      top: -8px;
      left: 50%;
      width: 14px;
      height: 14px;
      margin-left: -8px;
      background: @panel-sunken;
      border-top: 1px solid var(--accent);
      border-left: 1px solid var(--accent);
      transform: rotate(45deg);
    }

    @media @short {
      // Beside the player now, so point left at them instead.
      &::before {
        top: 50%;
        left: -8px;
        margin: -8px 0 0;
        transform: rotate(-45deg);
      }
    }

    .stat-label {
      .stat-label-rules();
      margin-bottom: 5px;
    }

    .bar-container,
    .plaque {
      .stat-frame();
    }

    .bar-container {
      .stat-bar();

      &.yellow {
        .stat-bar-yellow();
      }
    }

    .plaque {
      .stat-plaque();
    }

    .stat-keyframes();
  }

  // The price of losing, where the spoils would have been.
  .wounded {
    --icon-size: 16px;
    display: flex;
    align-items: center;
    grid-gap: 8px;
    margin: 0;
    font-size: 0.8rem;
    font-style: italic;
    color: rgba(255, 255, 255, 0.6);
  }

  .button-container {
    display: flex;
    justify-content: end;
    margin-top: clamp(10px, 2.5vh, 28px);
  }

  .button {
    white-space: nowrap;
    font-size: clamp(18px, min(4vh, 4.8vw), 28px);
    padding: 6px 12px;
    color: #ffffd5;
  }
</style>
